/**
 * Client-safe contact-form routing config (PPI-1703).
 *
 * This module is bundled into the browser via the "Contact Form" registered
 * component, so it MUST NOT contain destination email addresses. Nothing in the
 * request or this file names a mailbox: the browser sends ids, the server
 * forwards them, and a Zapier Lookup Table maps the pair to one of three fixed
 * mailboxes. That indirection is what stops the submission endpoint from being
 * an open relay — anything reachable from here is reachable by anyone with dev
 * tools.
 *
 * The `id` on every category and topic is the routing contract. Labels are
 * editable copy — marketing can rewrite them in Builder freely, because routing
 * keys on ids and never reads a label.
 */

export interface ContactTopic {
  /**
   * Stable routing key. Renaming one after publish orphans that row from the
   * Zap's Lookup Table until the table is updated to match — those submissions
   * then fall to the table's fallback mailbox rather than the intended one.
   * See docs/features/contact-form/ZAP-SETUP.md.
   */
  id: string;
  label: string;
  /** Phone number shown once this topic is selected. `null` renders no phone line. */
  phone: string | null;
}

export interface ContactCategory {
  /** Stable routing key — see ContactTopic.id. */
  id: string;
  label: string;
  /** Empty = no topic sub-dropdown, matching legacy behavior for single-row categories. */
  topics: ContactTopic[];
  /** Static paragraph rendered in place of a topic select. */
  staticParagraph?: string;
}

export const CONTACT_CATEGORY_MEMBER = "member";
export const CONTACT_CATEGORY_PURCHASING = "purchasing";
export const CONTACT_CATEGORY_AGENT_SUPPORT = "agent-support";
export const CONTACT_CATEGORY_AGENT_CONTRACTING = "agent-contracting";

/** Allied/IBA — shown for the three member topics that route to Member Success. */
const ALLIED_IBA_PHONE = "844-630-7500";
const AGENT_SUPPORT_PHONE = "866-566-2707";
const AGENT_CONTRACTING_PHONE = "480-542-9639";

/**
 * Default category/topic tree — the Builder `defaultValue` for the `categories`
 * input and ContactForm's fallback prop.
 *
 * Every sub-dropdown option named in PPI-1703 is its own row. Several rows share
 * a phone number and a destination mailbox, but they stay separate so the
 * submission records which topic the visitor actually picked — the legacy email
 * template printed that value, and collapsing them would lose it.
 */
export const DEFAULT_CONTACT_CATEGORIES: ContactCategory[] = [
  {
    id: CONTACT_CATEGORY_MEMBER,
    label: "Current Pivot Health Member",
    topics: [
      { id: "member-benefits", label: "Benefits", phone: ALLIED_IBA_PHONE },
      { id: "member-claims", label: "Claims", phone: ALLIED_IBA_PHONE },
      {
        id: "member-premium-payment",
        label: "Premium Payment/Credit Card",
        phone: ALLIED_IBA_PHONE,
      },
      {
        id: "member-id-card",
        label: "ID Card or other Policy Documentation",
        phone: null,
      },
      { id: "member-login-password", label: "Login/Password", phone: null },
      { id: "member-other", label: "Other", phone: null },
    ],
  },
  {
    id: CONTACT_CATEGORY_PURCHASING,
    label: "Interested in Purchasing a Pivot Health Policy",
    topics: [],
    staticParagraph:
      "If you are an individual interested in purchasing a policy, please call 866-484-9194. If you are an agent interested in helping your customer select a policy, please contact our Agent Support team at 866-566-2707 or at agentsupport@healthcare.com",
  },
  {
    id: CONTACT_CATEGORY_AGENT_SUPPORT,
    label: "Questions for Agent Support",
    topics: [
      {
        id: "agent-portal-login",
        label: "Help logging into the Pivot Health Agent Portal",
        phone: AGENT_SUPPORT_PHONE,
      },
      {
        id: "agent-commissions",
        label: "Questions about my commissions",
        phone: AGENT_CONTRACTING_PHONE,
      },
      {
        id: "agent-client-policy",
        label: "Questions about my client's policy",
        // PPI-1703 leaves the Member Success number blank for this row
        // (Open Item #1). No phone line renders until Product supplies one —
        // better than showing a wrong number.
        phone: null,
      },
      {
        id: "agent-carrier-appointment",
        label: "Questions about my carrier appointment",
        phone: AGENT_CONTRACTING_PHONE,
      },
      {
        id: "agent-training-products",
        label: "Questions about training or products",
        phone: AGENT_SUPPORT_PHONE,
      },
      {
        id: "agent-update-contact-info",
        label: "Need to update my agent contact information",
        phone: AGENT_CONTRACTING_PHONE,
      },
    ],
  },
  {
    id: CONTACT_CATEGORY_AGENT_CONTRACTING,
    // Ticket wording. The live Builder mock says "Agent Who Is Interested in
    // Contracting" instead (Open Item #4) — either is safe to set in Builder,
    // since routing keys on `id`.
    label: "Agents interested in Contracting with Pivot Health",
    topics: [],
  },
];

/**
 * Full US state list. Puerto Rico is excluded, matching the legacy `State`
 * class. Full names rather than abbreviations, because that is what the legacy
 * form posted and what the receiving mailboxes read.
 *
 * Duplicated server-side in `apps/dtc-v1/lib/contactValidation.ts` (that file
 * cannot import this one — see its header). A drift guard test keeps them equal.
 */
export const US_STATE_NAMES: string[] = [
  "Alabama",
  "Alaska",
  "Arizona",
  "Arkansas",
  "California",
  "Colorado",
  "Connecticut",
  "Delaware",
  "District of Columbia",
  "Florida",
  "Georgia",
  "Hawaii",
  "Idaho",
  "Illinois",
  "Indiana",
  "Iowa",
  "Kansas",
  "Kentucky",
  "Louisiana",
  "Maine",
  "Maryland",
  "Massachusetts",
  "Michigan",
  "Minnesota",
  "Mississippi",
  "Missouri",
  "Montana",
  "Nebraska",
  "Nevada",
  "New Hampshire",
  "New Jersey",
  "New Mexico",
  "New York",
  "North Carolina",
  "North Dakota",
  "Ohio",
  "Oklahoma",
  "Oregon",
  "Pennsylvania",
  "Rhode Island",
  "South Carolina",
  "South Dakota",
  "Tennessee",
  "Texas",
  "Utah",
  "Vermont",
  "Virginia",
  "Washington",
  "West Virginia",
  "Wisconsin",
  "Wyoming",
];

export function getCategory(
  categories: ContactCategory[],
  categoryId: string
): ContactCategory | undefined {
  return categories.find((category) => category.id === categoryId);
}

export function getTopic(
  category: ContactCategory | undefined,
  topicId: string
): ContactTopic | undefined {
  return category?.topics.find((topic) => topic.id === topicId);
}
